import { useCallback } from "react";
import type { EntityChangedEvent, EntityChangedKind, WebSocketMessage } from "@vms/types";
import { useCloudWebSocket, useRefetchOnReconnect } from "./ws";

/**
 * Refetch whenever the cloud broadcasts an entity-changed envelope for the
 * given entity. Pass `match` to filter by id or scope (e.g. only refetch
 * when the change concerns the venue/event the page is showing).
 *
 * Mirrors `apps/hub-ui/src/api/useInvalidateOn.ts` so leaderboard hooks lift
 * cleanly. Also refetches on WebSocket reconnect so a client that drops
 * mid-session catches up without manual refresh.
 */
export function useInvalidateOn(
  entity: EntityChangedKind,
  refetch: () => void,
  match?: (msg: EntityChangedEvent) => boolean,
): void {
  const handler = useCallback(
    (msg: WebSocketMessage) => {
      if (msg.type !== "entity-changed") return;
      if (msg.entity !== entity) return;
      if (match && !match(msg)) return;
      refetch();
    },
    [entity, match, refetch],
  );
  useCloudWebSocket(handler);
  useRefetchOnReconnect(refetch);
}
